/**
 * Shared currency helpers for billing UI.
 *
 * Stripe stores ``amount_off_cents`` (or any cents-denominated amount) in
 * the minor unit of the currency for most currencies (USD: 1¢ = 0.01 USD),
 * but in the major unit for zero-decimal currencies (JPY: 1 = 1 yen — no
 * subdivision). Dividing by 100 unconditionally produces 1/100th of the
 * real value for those currencies, so any code that turns ``cents`` into
 * formatted money must consult ``ZERO_DECIMAL_CURRENCIES``.
 *
 * See https://stripe.com/docs/currencies#zero-decimal
 */

export const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

export function isZeroDecimalCurrency(currency: string): boolean {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase());
}

/**
 * Format a cents-denominated amount in the given currency.
 *
 * For zero-decimal currencies, ``cents`` is treated as the major-unit
 * amount (no division). For decimal currencies, divides by 100. Drops
 * trailing ``.00`` when the amount is a whole major unit so "$25" reads
 * cleaner than "$25.00".
 */
export function formatMoney(cents: number, currency: string): string {
  const currencyCode = (currency || 'USD').toUpperCase();
  const isZeroDecimal = isZeroDecimalCurrency(currencyCode);
  const amount = isZeroDecimal ? cents : cents / 100;
  let minimumFractionDigits: number;
  if (isZeroDecimal) {
    minimumFractionDigits = 0;
  } else if (cents % 100 === 0) {
    minimumFractionDigits = 0;
  } else {
    minimumFractionDigits = 2;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  }).format(amount);
}
